"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import {
  getEffectiveUser,
  getStoredUser,
  getSessionToken,
} from "@/lib/session";

/** Public paths — never show member / ops footer links here */
const PUBLIC_FOOTER_PATHS = new Set([
  "/",
  "/enter",
  "/privacy",
  "/terms",
  "/trust",
  "/login",
]);

function isPublicPath(pathname: string): boolean {
  if (PUBLIC_FOOTER_PATHS.has(pathname)) return true;
  if (pathname.startsWith("/enter/")) return true;
  return false;
}

/**
 * Tier 1 and 1.5 — install, account security, source code browser places.
 */
function isLeadershipTier(tier: number | undefined | null): boolean {
  if (tier == null || Number.isNaN(Number(tier))) return false;
  const t = Number(tier);
  return t === 1 || t === 1.5;
}

/** Company ERP / Purchasing (includes Tier 2 owners + team) */
function canCompanyErpTier(tier: number | undefined | null): boolean {
  if (tier == null || Number.isNaN(Number(tier))) return false;
  const t = Number(tier);
  return t === 1 || t === 1.5 || t === 2 || t === 2.5 || t === 3 || t === 3.5;
}

/** Tier 1 only — vault, Earn, Security ops places */
function isRootTier(tier: number | undefined | null): boolean {
  return Number(tier) === 1;
}

/**
 * Public footer: company + legal + Trust only.
 * Member places follow **UI tier preview** (effective user), so previewing
 * Tier 4 does not leave Tier 1 places in the footer.
 * Real API access is still gated by real account on each page/route.
 */
export function SiteFooter() {
  const pathname = usePathname();
  const [tier, setTier] = useState<number | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);

  function refreshTierUi() {
    const token = getSessionToken();
    const user = getStoredUser();
    const ok = Boolean(token && user);
    setLoggedIn(ok);
    if (!ok) {
      setTier(null);
      return;
    }
    const eff = getEffectiveUser();
    setTier(eff?.tier != null ? Number(eff.tier) : null);
  }

  useEffect(() => {
    refreshTierUi();
    const onView = () => refreshTierUi();
    window.addEventListener("virtuous:view-change", onView);
    window.addEventListener("storage", onView);
    return () => {
      window.removeEventListener("virtuous:view-change", onView);
      window.removeEventListener("storage", onView);
    };
  }, [pathname]);

  const publicPage = isPublicPath(pathname);
  // Places only for the preview/effective tier — not leftover higher-tier links
  const showLeadershipTools =
    loggedIn && !publicPage && isLeadershipTier(tier);
  const showCompanyErp =
    loggedIn && !publicPage && canCompanyErpTier(tier);
  const showRootOps = loggedIn && !publicPage && isRootTier(tier);

  return (
    <footer className="site-footer px-4 py-6 text-xs">
      <div className="mx-auto max-w-6xl space-y-1.5 text-left">
        <div className="block">Virtuous Trade &amp; Consulting LLC</div>
        <div className="block">
          <a href="/privacy" className="hover:text-[var(--accent)]">
            Privacy Policy
          </a>
        </div>
        <div className="block">
          <a href="/terms" className="hover:text-[var(--accent)]">
            Terms of Use
          </a>
        </div>
        <div className="block">
          <a href="/trust" className="hover:text-[var(--accent)]">
            Trust &amp; compliance
          </a>
        </div>

        {showCompanyErp ? (
          <div className="block">
            <a href="/integrations" className="hover:text-[var(--accent)]">
              Company ERP · Purchasing
            </a>
          </div>
        ) : null}

        {/* Source code / install / account: Tier 1 and 1.5 */}
        {showLeadershipTools ? (
          <>
            <div className="block">
              <a href="/source-code" className="hover:text-[var(--accent)]">
                Source code
              </a>
            </div>
            <div className="block">
              <a href="/install" className="hover:text-[var(--accent)]">
                Install app
              </a>
            </div>
            <div className="block">
              <a href="/account" className="hover:text-[var(--accent)]">
                Account security
              </a>
            </div>
          </>
        ) : null}

        {/* Password vault + Earn / Security: Tier 1 only */}
        {showRootOps ? (
          <>
            <div className="block">
              <a href="/vault" className="hover:text-[var(--accent)]">
                Password vault
              </a>
            </div>
            <div className="block">
              <a href="/earn" className="hover:text-[var(--accent)]">
                Earn
              </a>
            </div>
            <div className="block">
              <a href="/security" className="hover:text-[var(--accent)]">
                Security ops
              </a>
            </div>
          </>
        ) : null}

        <div className="block text-[var(--faint)]">virtuous.work</div>
      </div>
    </footer>
  );
}
